'use client';
import {getStrapiData} from '@/app/lib/api';
import {useMenu} from '@/context/MenuContext';
import clsx from 'clsx';
import {useLocale, useTranslations} from 'next-intl';
import {useEffect, useMemo, useState} from 'react';
import TagManager from 'react-gtm-module';

export default function Footer() {
  const t = useTranslations('footer');
  const locale = useLocale();
  const year = new Date().getFullYear().toString();

  const [organisations, setOrganisations] = useState([]);
  const [showFooter, setShowFooter] = useState(false);

  const supportOrganisations = useMemo(() => {
    return organisations.filter((o: any) => o.type === 'Supporting');
  }, [organisations]);

  const coorganisers = useMemo(() => {
    return organisations.filter((o: any) => o.type === 'Co-organisers');
  }, [organisations]);

  const [allPostUrl, setAllPostUrl] = useState('');
  useEffect(() => {
    (async () => {
      await getStrapiData(locale, 'organisations', {
        populate: ['image'],
        sort: ['priority:desc', 'createdAt:asc'],
        pagination: {page: 1, pageSize: 100}
      }).then((data) => {
        setOrganisations(data.data);
      });
    })();
  }, [locale]);

  useEffect(() => {
    TagManager.initialize({
      gtmId: 'GTM-T5BL7L'
    });
    TagManager.dataLayer({
      dataLayer: {
        event: 'pageview',
        pagePath: 'page-path-here',
        pageTitle: 'page-title-here'
      }
    });

    getStrapiData(locale, 'posts', {
      populate: ['thumbnail'],
      sort: 'posted_at:desc'
    }).then((r) => {
      if (r.data) {
        setAllPostUrl('/article/' + r.data?.[0]?.slug);
      }
    });

    const urlParams = new URLSearchParams(window.location.search);
    if (!window.location.pathname.includes('/nomination/')) {
      if (urlParams.get('popup') !== '1') {
        setShowFooter(true);
      }
    }
  }, []);
  const {hideMenu, isShowMobileMenu} = useMenu();

  return (
    showFooter &&
    !isShowMobileMenu && (
      <footer className="flex flex-col common-layout footer">
        <div id="mobile-footer">
          <div className="md:hidden">
            <div className="bg-[#131313] w-full py-[6vw] px-[7vw]">
              <div className="grid grid-cols-2 items-start max-w-[440px] m-auto">
                <div className="flex text-[3.5vw] mb-[5vw] col-span-2 items-start">
                  <div className="w-full">
                    <div className="footer-title">{t('menu.organisers')}</div>
                    <div className="flex gap-[2vw] flex-col items-center justify-start w-full">
                      <a
                        href="https://www.aswatson.com/"
                        className="flex items-center justify-start gap-2 w-full"
                      >
                        <img
                          className="h-[50px] object-contain"
                          src="/assets/images/footer/as-watson-group.png"
                          alt={t('aswatsonGroup')}
                        />

                        <img
                          className="h-[50px] object-contain"
                          src="/assets/images/footer/as-watson-185-yrs-zh.png"
                          alt={t('aswatsonGroup185')}
                        />
                      </a>
                      <a
                        href="https://www.ckh.com.hk/​"
                        className="flex items-center justify-start gap-2 w-full"
                      >
                        <img
                          className="h-[35px] object-contain"
                          src="/assets/images/footer/asw-corporate-endorsement-white.png"
                          alt={t('ckh')}
                        />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="col-span-2">
                  <div className="footer-title"> {t('menu.coorganisers')}</div>
                  <div className="flex flex-col-2 gap-[1vw]  mb-[5vw]">
                    {coorganisers.map((o: any) => (
                      <div key={'co-organiser-m-' + o.slug} className="w-1/2">
                        <a href="/about/supporting-organizations/">
                          <div className="flex flex-col-2 gap-[0.1vw] justify-between items-center">
                            <div className="w-1/3 pr-3">
                              <img
                                src={o?.image?.url}
                                className="w-full object-contain"
                                alt={o.title}
                              />
                            </div>
                            <div
                              className={clsx(
                                'w-2/3 flex flex-col gap-[2px]',
                                locale == 'zh' && 'flex-col-reverse'
                              )}
                            >
                              <div className="text-[0.7rem] leading-[2.5vw] flex items-center line-clamp-2 mb-1">
                                {o.title}
                              </div>
                              <div
                                className="text-[0.5rem]"
                                dangerouslySetInnerHTML={{__html: o.belong}}
                              ></div>
                            </div>
                          </div>
                        </a>
                      </div>
                    ))}
                  </div>
                  <div className="footer-title">
                    {t('menu.supportingOrganisations')}
                  </div>
                  <div className="mb-[5vw]">
                    <a href="/about/supporting-organizations/">
                      <div className="grid grid-cols-6 gap-[2vw]">
                        {supportOrganisations.map((o: any) => (
                          <div key={o.slug}>
                            <img
                              src={o?.image?.url}
                              className="w-full object-contain"
                              alt={o.title}
                            />
                          </div>
                        ))}
                      </div>
                    </a>
                  </div>
                </div>
                <div className="footer-title">{t('menu.aboutSSA')}</div>
                <div className="footer-title"> {t('menu.activity')}</div>
                <div className="footer-subtitle text-sm flex flex-col gap-[5vw] mb-[5vw]">
                  <a href="/about/advisory-committee/">
                    {t('subMenu.advisoryCommittee')}
                  </a>
                  <a href="/about/they-say/">{t('subMenu.theySay')}</a>
                  <a href="/about/supporting-organizations/">
                    {t('subMenu.supportingOrganisations')}
                  </a>
                </div>
                <div className="footer-subtitle text-sm flex flex-col gap-[5vw] mb-[5vw]">
                  <a href="/activity/#nomination">
                    {t('subMenu.schoolNomination')}
                  </a>
                  <a href="/activity/#you-are-star">
                    {t('subMenu.sportsPhotography')}
                  </a>
                  <a href="/activity/#presentation">
                    {t('subMenu.awardsPresentationCeremony')}
                  </a>
                  <a href="/activity/#tour">
                    {' '}
                    {t('subMenu.sportsExchangeTour')}
                  </a>
                  <a href="/activity/#FAQ">{t('subMenu.faq')}</a>
                </div>
                <div className="footer-title">{t('menu.announcements')}</div>
                <div className="footer-title"></div>
                <div className="footer-subtitle text-sm flex flex-col gap-[5vw] mb-[5vw]">
                  <a href="/publication/">{t('subMenu.publication')}</a>
                  <a href={allPostUrl}>{t('subMenu.pressRelease')}</a>
                </div>
              </div>
            </div>
            <div>
              <div className="bg-[#E2231A] flex flex-col justify-center items-center w-full py-[2vw]">
                <div className="flex items-center gap-4 py-[6vw]">
                  <a href="https://www.facebook.com/aswhkssa">
                    <div className="w-[9vw] h-[9vw] bg-white rounded-full flex justify-center items-center">
                      <img
                        src="/assets/images/footer/footer-fb.png"
                        className="h-[5.5vw]"
                        alt="Facebook"
                      />
                    </div>
                  </a>
                  <a href="https://www.instagram.com/aswhkssa/">
                    <div className="w-[9vw] h-[9vw] bg-white rounded-full flex justify-center items-center">
                      <img
                        src="/assets/images/footer/footer-ig.png"
                        className="h-[5.5vw]"
                        alt="Instagram"
                      />
                    </div>
                  </a>
                </div>

                <div className="flex justify-center items-center gap-[5vw] text-white mb-[5vw]">
                  <div className="flex gap-[1vw] items-center">
                    <div>
                      <img
                        src="/assets/images/footer/icon-mobile.png"
                        className="h-[3.0vw]"
                        alt={t('contact')}
                      />
                    </div>
                    <div className="footer-subtitle text-sm">
                      (852) 3521 6225
                    </div>
                  </div>
                  <div className="flex gap-[1vw] items-center">
                    <div>
                      <img
                        src="/assets/images/footer/icon-email.png"
                        className="h-[3.5vw]"
                        alt={t('email')}
                      />
                    </div>
                    <div>
                      <a
                        href="mailto:SSA@aswatson.com"
                        className="footer-subtitle text-sm"
                      >
                        SSA@aswatson.com
                      </a>
                    </div>
                  </div>
                </div>
                <div className="footer-subtitle text-sm text-white mb-[5vw]">
                  {t('copyright', {y: year})}
                  <div className="flex justify-center items-center gap-[1vw] mt-[1vw]">
                    <a href="/disclaimer/"> {t('disclaimer')} </a>|{' '}
                    <a href="/accessibility-statement/">
                      {t('accessibilityStatement')}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="footer">
          <div
            className="hidden md:flex md:flex-col"
            onMouseEnter={() => hideMenu()}
          >
            <div className="bg-[#131313] text-white w-full flex justify-between items-center py-[6vw] px-[11vw]">
              <div className="flex items-start gap-[3vw] w-full">
                <div className="grid grid-cols-3 w-1/2 gap-8">
                  <div className="col-span-3 xl:col-span-1">
                    <div className="flex justify-between gap-[7vw]">
                      <div className="footer-title-l text-lg">
                        {t('menu.organisers')}
                      </div>
                    </div>
                    <div className="flex mb-[2vw] gap-[0.8vw] flex-col">
                      <a
                        href="https://www.aswatson.com/"
                        target="_blank"
                        className="flex items-center justify-start gap-2 w-full"
                      >
                        <img
                          src="/assets/images/footer/as-watson-group.png"
                          className="object-contain max-h-10 max-w-3/5"
                          alt={t('aswatsonGroup')}
                        />

                        <img
                          className="max-h-9 object-contain max-w-2/5"
                          src="/assets/images/footer/as-watson-185-yrs-zh.png"
                          alt={t('aswatsonGroup185')}
                        />
                      </a>
                      <a href="https://www.ckh.com.hk" target="_blank">
                        <img
                          src="/assets/images/footer/asw-corporate-endorsement-white.png"
                          className="h-9 object-contain"
                          alt={t('ckh')}
                        />
                      </a>
                    </div>
                  </div>
                  <div className="col-span-3 xl:col-span-2">
                    <div className="flex flex-col justify-between">
                      <div className="footer-title-l text-lg">
                        {t('menu.coorganisersAndSupportingOrganisations')}
                      </div>

                      <a href="/about/supporting-organizations/">
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-[1vw] mb-[1vw]  md:w-2/3 lg:w-1/2 xl:w-full">
                          {coorganisers.map((o: any) => (
                            <div
                              className="grid grid-cols-4 gap-1 justify-start items-center"
                              key={o.slug}
                            >
                              <div className="p-1">
                                <img
                                  src={o?.image?.url}
                                  className="h-9 object-contain"
                                  alt={o.title}
                                />
                              </div>
                              <div
                                className={clsx(
                                  'col-span-3 flex flex-col gap-[2px]',
                                  locale == 'zh' && 'flex-col-reverse '
                                )}
                              >
                                <div className="text-sm xl:text-[0.7vw] flex items-center leading-none">
                                  {o.title}
                                </div>
                                <div
                                  className="text-[0.5rem] xl:text-[0.4vw] mb-1"
                                  dangerouslySetInnerHTML={{__html: o.belong}}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-6 w-[300px] ">
                          {supportOrganisations.map((o: any) => (
                            <img
                              src={o?.image?.url}
                              className="h-10 w-10 object-contain mb-3 bg-white"
                              key={'img-' + o.slug}
                              alt={o.title}
                            />
                          ))}
                        </div>
                      </a>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 xl:grid-cols-3 justify-end gap-[3vw] w-1/2">
                  <div className="flex flex-col items-start">
                    <div className="footer-title-r text-lg">
                      {t('menu.announcements')}
                    </div>
                    <div className="footer-subtitle-group flex flex-col gap-5">
                      <a
                        href="/publication/"
                        className="footer-subtitle text-sm"
                      >
                        {t('subMenu.publication')}
                      </a>
                      <a href={allPostUrl} className="footer-subtitle text-sm">
                        {t('subMenu.pressRelease')}
                      </a>
                    </div>
                  </div>
                  <div className="flex flex-col items-start">
                    <div className="footer-title-r text-lg">
                      {t('menu.aboutSSA')}
                    </div>
                    <div className="footer-subtitle-group flex flex-col gap-5">
                      <a
                        href="/about/advisory-committee/"
                        className="footer-subtitle text-sm"
                      >
                        {t('subMenu.advisoryCommittee')}
                      </a>
                      <a
                        href="/about/they-say/"
                        className="footer-subtitle text-sm"
                      >
                        {t('subMenu.theySay')}
                      </a>
                      <a
                        href="/about/supporting-organizations/"
                        className="footer-subtitle text-sm"
                      >
                        {t('subMenu.supportingOrganisations')}
                      </a>
                    </div>
                  </div>
                  <div className="flex flex-col items-start">
                    <div className="footer-title-r text-lg">
                      {t('menu.activity')}
                    </div>
                    <div className="footer-subtitle-group flex flex-col gap-5">
                      <a
                        href="/activity/#nomination"
                        className="footer-subtitle text-sm"
                      >
                        {t('subMenu.schoolNomination')}
                      </a>
                      <a
                        href="/activity/#you-are-star"
                        className="footer-subtitle text-sm"
                      >
                        {t('subMenu.sportsPhotography')}
                      </a>
                      <a
                        href="/activity/#presentation"
                        className="footer-subtitle text-sm"
                      >
                        {t('subMenu.awardsPresentationCeremony')}
                      </a>
                      <a
                        href="/activity/#tour"
                        className="footer-subtitle text-sm"
                      >
                        {t('subMenu.sportsExchangeTour')}
                      </a>
                      <a
                        href="/activity/#FAQ"
                        className="footer-subtitle text-sm"
                      >
                        {t('subMenu.faq')}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="bg-[#E2231A] flex justify-between items-center w-full py-[2vw] px-[11vw]">
                <div className="text-[0.75rem] text-white">
                  {t('copyright', {y: year})} |{' '}
                  <a href="/disclaimer/"> {t('disclaimer')} </a> |{' '}
                  <a href="/accessibility-statement/">
                    {t('accessibilityStatement')}
                  </a>
                </div>

                <div className="flex items-center gap-4 flex-none">
                  <div className="text-[0.75rem] text-white flex items-center mr-[2vw] gap-[1vw]">
                    <div className="flex items-center gap-[0.5vw]">
                      <img
                        src="/assets/images/footer/icon-mobile.png"
                        className="h-[0.8vw]"
                        alt={t('contact')}
                      />
                      <a>(852) 3521 6225</a>
                    </div>
                    <div className="flex items-center gap-[0.5vw]">
                      <img
                        src="/assets/images/footer/icon-email.png"
                        className="h-[1vw]"
                        alt={t('email')}
                      />
                      <a href="mailto:SSA@aswatson.com">SSA@aswatson.com</a>
                    </div>
                  </div>
                  <a href="https://www.facebook.com/aswhkssa">
                    <div className="w-[2.5vw] h-[2.5vw] bg-white rounded-full flex justify-center items-center">
                      <img
                        src="/assets/images/footer/footer-fb.png"
                        className="h-[1.4vw]"
                        alt="Facebook"
                      />
                    </div>
                  </a>
                  <a href="https://www.instagram.com/aswhkssa/">
                    <div className="w-[2.5vw] h-[2.5vw] bg-white rounded-full flex justify-center items-center">
                      <img
                        src="/assets/images/footer/footer-ig.png"
                        className="h-[1.4vw]"
                        alt="Instagram"
                      />
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    )
  );
}
